import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';

const SpecialOfferSection = () => {
  const navigate = useNavigate();

  const basicFunctions = [
    "Secure admin access with session protection",
    "Manage multiple sales projects independently",
    "Import leads from Excel & CSV files",
    "Email templates with personalization tags",
    "Schedule & send email campaigns",
    "Complete send history & tracking",
    "Automatic unsubscribe handling",
    "Separate sender identity per project"
  ];

  const advancedFunctions = [
    "Delivery & open tracking analytics",
    "Automatic follow-up campaigns",
    "AI-powered template creator",
    "Email finder & verification",
    "Advanced email address validator",
    "Visitor analytics dashboard",
    "SEO ranking tracker",
    "Real-time campaign performance metrics"
  ];

  return (
    <section style={{ 
      background: 'linear-gradient(135deg, #0D2C22 0%, #1a4433 100%)',
      padding: '100px 7.6923%',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background elements */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '300px',
        height: '300px',
        background: 'rgba(229, 169, 61, 0.1)',
        borderRadius: '50%',
        filter: 'blur(80px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-100px',
        left: '-100px',
        width: '400px',
        height: '400px',
        background: 'rgba(229, 169, 61, 0.05)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Special Offer Badge */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#E5A93D',
            color: '#0D2C22',
            padding: '8px 20px',
            borderRadius: '50px',
            fontSize: '14px',
            fontWeight: '700',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            boxShadow: '0 4px 20px rgba(229, 169, 61, 0.4)'
          }}>
            <Sparkles size={16} />
            <span>Special Offer for Small Businesses</span>
          </div>
        </div>

        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h2 className="font-display" style={{ 
            fontSize: '48px', 
            fontWeight: '700', 
            color: '#FFFFFF',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            Transform Your Sales Outreach
          </h2>
          <p style={{ 
            fontSize: '20px', 
            color: 'rgba(255, 255, 255, 0.85)',
            maxWidth: '800px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Reach more customers, save time on marketing, and grow your business with our customized sales leads system—designed specifically for small companies who want professional results at an affordable price.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '10px'
          }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '12px 24px',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px' }}>Core System</div>
              <div style={{ color: '#E5A93D' }}>€450 setup + €20/month</div>
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic', marginTop: '4px' }}>
                The small monthly subscription fee covers keeping your sales system running.
              </div>
            </div>
            <div style={{
              display: 'inline-block',
              background: 'rgba(229, 169, 61, 0.2)',
              border: '2px solid #E5A93D',
              padding: '12px 24px',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '4px' }}>Advanced System</div>
              <div style={{ color: '#E5A93D' }}>€850 setup + €20/month</div>
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic', marginTop: '4px' }}>
                The small monthly subscription fee covers keeping your sales system running.
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Features */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '40px',
          marginBottom: '50px'
        }}>
          {/* Basic Functions */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            padding: '40px',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ marginBottom: '30px' }}>
              <h3 className="font-display" style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#FFFFFF',
                marginBottom: '12px'
              }}>
                Core Features
              </h3>
              <p style={{ 
                fontSize: '16px', 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.5'
              }}>
                Everything you need to start professional email campaigns
              </p>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {basicFunctions.map((feature, idx) => (
                <li key={idx} style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px',
                  marginBottom: '16px',
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.5'
                }}>
                  <CheckCircle 
                    size={20} 
                    style={{ 
                      color: '#E5A93D', 
                      flexShrink: 0,
                      marginTop: '2px'
                    }} 
                  />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Advanced Functions */}
          <div style={{
            background: 'rgba(229, 169, 61, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '2px solid #E5A93D',
            borderRadius: '20px',
            padding: '40px',
            position: 'relative',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(229, 169, 61, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            {/* Premium Badge */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              right: '30px',
              background: '#E5A93D',
              color: '#0D2C22',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Premium
            </div>
            <div style={{ marginBottom: '30px' }}>
              <h3 className="font-display" style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#FFFFFF',
                marginBottom: '12px'
              }}>
                Advanced Features
              </h3>
              <p style={{ 
                fontSize: '16px', 
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: '1.5'
              }}>
                Powerful tools to maximize your results and ROI
              </p>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {advancedFunctions.map((feature, idx) => (
                <li key={idx} style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px',
                  marginBottom: '16px',
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: '1.5'
                }}>
                  <CheckCircle 
                    size={20} 
                    style={{ 
                      color: '#E5A93D', 
                      flexShrink: 0,
                      marginTop: '2px'
                    }} 
                  />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            <p style={{ 
              fontSize: '18px', 
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              <strong style={{ color: '#E5A93D' }}>Limited Time:</strong> Get your customized sales leads system with professional features. Choose Core (€450 + €20/month) or Advanced (€850 + €20/month). Perfect for small businesses ready to scale their outreach affordably.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/sales-leads-pro')}
                style={{
                  background: '#E5A93D',
                  color: '#0D2C22',
                  border: 'none',
                  padding: '18px 40px',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 20px rgba(229, 169, 61, 0.4)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#d99a35';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 25px rgba(229, 169, 61, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#E5A93D';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(229, 169, 61, 0.4)';
                }}
              >
                Get Your Custom Quote
                <ArrowRight size={20} />
              </button>
              <button
                onClick={() => {
                  document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  background: 'transparent',
                  color: '#FFFFFF',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  padding: '16px 40px',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#E5A93D';
                  e.target.style.background = 'rgba(229, 169, 61, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  e.target.style.background = 'transparent';
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecialOfferSection;
